import React, { useRef, useState } from 'react';
import { Send, Camera, Loader2, X, Check, Trash2, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import nutritionApi from '../../services/nutritionApi';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'preworkout', 'postworkout'];

const ChatInput = ({ onSend, disabled }) => {
  const [v, setV] = useState('');
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null);   // {items, notes, totals, source, photo_path?}
  const [mealType, setMealType] = useState('snack');
  const [saving, setSaving] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const text = v.trim();
    if (!text || disabled) return;
    onSend?.(text);
    setV('');
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo too large (max 5MB)'); return; }
    setParsing(true);
    try {
      const r = await nutritionApi.parsePhoto(file);
      if (!r.data.items?.length) {
        toast.error("I couldn't identify food in that photo — try another angle");
        return;
      }
      setDraft({ ...r.data, source: 'photo' });
    } catch {
      toast.error('Photo analysis failed');
    } finally {
      setParsing(false);
    }
  };

  const updateItem = (idx, key, val) =>
    setDraft(d => {
      const items = [...d.items];
      const numeric = ['kcal', 'protein_g', 'carb_g', 'fat_g'];
      items[idx] = { ...items[idx], [key]: numeric.includes(key) ? Number(val) || 0 : val };
      return { ...d, items };
    });
  const removeItem = (idx) =>
    setDraft(d => {
      const items = d.items.filter((_, i) => i !== idx);
      return items.length ? { ...d, items } : null;
    });
  const addItem = () =>
    setDraft(d => ({
      ...d,
      items: [...(d?.items || []), { name: '', quantity: '', kcal: 0, protein_g: 0, carb_g: 0, fat_g: 0, confidence: 'medium' }],
    }));

  const saveMeal = async () => {
    if (!draft?.items?.length) return;
    setSaving(true);
    try {
      await nutritionApi.log({
        items: draft.items,
        meal_type: mealType,
        source: draft.source,
        photo_path: draft.photo_path || null,
        notes: draft.notes || null,
      });
      toast.success('Meal logged');
      setDraft(null);
      // Nudge the chat: send a system message so the thread reflects the action
      onSend?.('Logged that meal — what should I look at next?');
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-zinc-800 bg-black/60 backdrop-blur" data-testid="chat-input">
      {draft && (
        <div className="px-4 md:px-8 py-3 border-b border-zinc-800 bg-zinc-900/60" data-testid="chat-meal-draft">
          <div className="max-w-3xl mx-auto space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm uppercase tracking-wider text-zinc-400">Confirm meal</h3>
              <button onClick={() => setDraft(null)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            {draft.notes && <p className="text-xs text-zinc-500 italic">{draft.notes}</p>}
            <div className="space-y-2">
              {draft.items.map((it, idx) => (
                <div key={idx} className="rounded-sm border border-zinc-800 bg-zinc-950/50 p-2.5 space-y-2" data-testid={`chat-draft-item-${idx}`}>
                  <div className="flex items-center gap-2">
                    <Input className="flex-1 h-9 text-sm" value={it.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="food name" data-testid={`chat-draft-name-${idx}`} />
                    <Input className="w-20 h-9 text-sm shrink-0" value={it.quantity || ''} onChange={e => updateItem(idx, 'quantity', e.target.value)} placeholder="qty" data-testid={`chat-draft-qty-${idx}`} />
                    <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 shrink-0 p-1" data-testid={`chat-draft-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[['kcal', 'kcal'], ['protein_g', 'P (g)'], ['carb_g', 'C (g)'], ['fat_g', 'F (g)']].map(([k, lab]) => (
                      <label key={k} className="flex flex-col gap-0.5">
                        <span className="text-[9px] uppercase tracking-wide text-zinc-500">{lab}</span>
                        <Input type="number" inputMode="numeric" className="h-9 text-sm" value={it[k]} onChange={e => updateItem(idx, k, e.target.value)} data-testid={`chat-draft-${k}-${idx}`} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <select
                  value={mealType}
                  onChange={e => setMealType(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs"
                  data-testid="chat-draft-meal-type"
                >
                  {MEAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <Button size="sm" variant="ghost" onClick={addItem} className="h-7 px-2 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> add
                </Button>
              </div>
              <Button size="sm" onClick={saveMeal} disabled={saving} className="bg-primary text-black h-7" data-testid="chat-save-meal-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                Save meal
              </Button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="px-4 md:px-8 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 focus-within:border-primary transition-colors">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={parsing || disabled || !!draft}
            className="text-zinc-400 hover:text-primary disabled:text-zinc-700 p-1 rounded transition-colors"
            title="Photo of plate"
            data-testid="chat-photo-btn"
          >
            {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={handlePhoto}
            className="hidden"
            data-testid="chat-photo-input"
          />
          <input
            type="text"
            value={v}
            onChange={e => setV(e.target.value)}
            disabled={disabled}
            placeholder="Say something to Nikki…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-500"
            data-testid="chat-input-field"
          />
          <button type="submit" disabled={!v.trim() || disabled}
            className="text-primary disabled:text-zinc-600 hover:bg-primary/10 p-1.5 rounded-sm transition-colors"
            data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
